import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useCategoryOptions } from '@/features/categories';
import { ApiError } from '@/shared/api/api-error';
import { SearchableSelect, type SearchableOption } from '@/shared/components/searchable-select';

import { brandSchema, type BrandFormValues } from '../schemas/brand.schema';
import type { BrandHeader } from '../types/brand.types';

/** Admin API reason codes that belong to one field. */
const FIELD_FOR_CODE: Partial<Record<string, keyof BrandFormValues>> = {
  BRAND_NAME_TAKEN: 'name',
  CATEGORY_NOT_FOUND: 'categoryId',
};

/**
 * Create / edit form for a brand.
 *
 * One component serves both modes. They differ only in default values and
 * button label, and splitting them would duplicate every field for no gain.
 *
 * Written explicitly with `<FormField>` per input rather than generated from a
 * schema: business forms accumulate per-field quirks (the RTL Arabic input),
 * and a metadata-driven form framework ends up encoding each quirk as a config
 * flag nobody can follow (§47).
 *
 * There is no position field: a new brand goes after the last one, and the
 * storefront order is changed only in "Storefront brand order".
 */
export function BrandForm({
  brand,
  isPending,
  error,
  onSubmit,
  onCancel,
}: {
  /** Omit for create mode. */
  brand?: BrandHeader;
  isPending: boolean;
  error?: unknown;
  onSubmit: (values: BrandFormValues) => void;
  onCancel: () => void;
}) {
  const categories = useCategoryOptions();
  const isEdit = brand !== undefined;
  // An empty optional field must not look filled in: when editing, say it is not set;
  // when creating, mark the hint as an example.
  const example = (text: string) => (isEdit ? 'Not set' : `e.g. ${text}`);

  const form = useForm<BrandFormValues>({
    resolver: zodResolver(brandSchema),
    defaultValues: {
      name: brand?.name ?? '',
      arName: brand?.arName ?? '',
      categoryId: brand?.category.id ?? 0,
      slug: brand?.slug ?? '',
      description: brand?.description ?? '',
      imageUrl: brand?.imageUrl ?? '',
      // New brands start inactive so they can be prepared before customers see them.
      isActive: brand?.isActive ?? false,
    },
  });

  /**
   * Project server-side validation errors onto the matching fields.
   *
   * The backend validates independently of the Zod schema: model validation
   * returns per-field errors, and business rules (a taken name, an unknown
   * category) return a reason code that belongs to one field. Without this
   * the user would see no indication of which field was wrong (§45).
   */
  useEffect(() => {
    if (!(error instanceof ApiError)) return;

    const codeField = error.code ? FIELD_FOR_CODE[error.code] : undefined;
    if (codeField) {
      form.setError(codeField, { type: 'server', message: error.message });
      return;
    }
    if (!error.fieldErrors) return;

    for (const [field, message] of Object.entries(error.fieldErrors)) {
      // Backend field names are PascalCase; form fields are camelCase.
      const key = (field.charAt(0).toLowerCase() + field.slice(1)) as keyof BrandFormValues;
      if (key in form.getValues()) {
        form.setError(key, { type: 'server', message });
      }
    }
  }, [error, form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <div className="grid items-start gap-5 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder={example('Google Play')}
                    autoFocus
                    disabled={isPending}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="arName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Arabic name</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    dir="rtl"
                    placeholder={example('جوجل بلاي')}
                    disabled={isPending}
                  />
                </FormControl>
                <FormDescription>Shown on the Arabic storefront.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="categoryId"
            render={({ field }) => {
              // Show every category that has an English name, plus the brand's
              // current one even if it lost its translation, so an existing
              // value is never silently dropped from the list.
              const options: SearchableOption[] = (categories.data ?? [])
                .filter((category) => category.hasEnglishTranslation || category.id === field.value)
                .map((category) => ({
                  value: String(category.id),
                  label: category.isActive ? category.name : `${category.name} (inactive)`,
                }));

              return (
                <FormItem>
                  <FormLabel htmlFor="brand-category">Category</FormLabel>
                  <SearchableSelect
                    id="brand-category"
                    label="Category"
                    value={field.value ? String(field.value) : null}
                    options={options}
                    onChange={(value) => field.onChange(Number(value))}
                    placeholder="Select a category"
                    searchPlaceholder="Search categories…"
                    emptyText="No category matches."
                    disabled={isPending || categories.isLoading}
                    loading={categories.isLoading}
                    triggerClassName="h-9 text-sm"
                  />
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              // Coming soon: nothing reads Brands.Slug yet — neither storefront
              // builds brand URLs from it. The field stays visible (and an existing
              // value is still sent back unchanged) so the feature is not forgotten.
              // Enable it once the storefront uses slugs.
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>Slug</FormLabel>
                  <span className="text-muted-foreground border-border rounded border px-1 py-px text-[9px] font-medium tracking-wide uppercase">
                    Soon
                  </span>
                </div>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    placeholder="Coming soon"
                    className="font-mono text-xs"
                    disabled
                  />
                </FormControl>
                <FormDescription>Not used yet — planned for storefront brand URLs.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Full-width fields come after the paired single-column ones, so a
              col-span-2 field never orphans an empty half-cell beside a
              single-column field above it. */}
          <FormField
            control={form.control}
            name="imageUrl"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>Logo URL</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    placeholder={example('https://cdn.digital-codex.com/brands/google-play.png')}
                    disabled={isPending}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ''} disabled={isPending} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {!isEdit && (
            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="border-border flex flex-row items-center justify-between rounded-lg border p-3 sm:col-span-2">
                  <div className="space-y-0.5">
                    <FormLabel>Active on creation</FormLabel>
                    <FormDescription>
                      Leave off to prepare the brand and its denominations before customers see it.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isPending}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending || !form.formState.isDirty}>
            {isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
            {isEdit ? 'Save changes' : 'Create brand'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
